import {
  CuttingDeleteAckSchema,
  CuttingDraftListResponseSchema,
  CuttingDraftPromoteSchema,
  CuttingDraftSchema,
  CuttingDraftUpsertSchema,
  CuttingProjectListQuerySchema,
  CuttingProjectListResponseSchema,
  CuttingProjectSchema,
  CuttingProjectUpsertSchema,
  type CuttingDeleteAck,
  type CuttingDraft,
  type CuttingDraftListResponse,
  type CuttingDraftPromoteInput,
  type CuttingDraftUpsert,
  type CuttingProject,
  type CuttingProjectListQuery,
  type CuttingProjectListResponse,
  type CuttingProjectUpsertInput,
} from '../contracts/cutting';
import type { RequestFn } from './client';

/**
 * Block 14 «Раскрой» — apiClient namespace `cutting` (ADR-0065, EP-1).
 *
 * Эндпоинты (GET/POST only — `client.ts` не правится, handover §3; идиома
 * idempotent-upsert по client-UUID как `calculator`, ADR-0019 §Q11.3):
 *   - `GET  /api/cutting/projects`            → список (cursor-пагинация + status/search)
 *   - `GET  /api/cutting/projects/:id`        → CuttingProject
 *   - `POST /api/cutting/projects`            → upsert → CuttingProject
 *   - `POST /api/cutting/projects/:id/delete` → soft-delete → CuttingDeleteAck
 *   - `GET  /api/cutting/drafts`              → полные драфты (рабочий набор)
 *   - `POST /api/cutting/drafts`              → upsert → CuttingDraft
 *   - `POST /api/cutting/drafts/:id/promote`  → project из draft.data + удалить драфт → CuttingProject
 *
 * **Auth (P-009):** все вызовы `auth: true`. Раскрой бесплатен (без подписки), но
 * требует JWT — 401 без него (TRD §4.2). Paywall НЕ применяется.
 *
 * **Client-side Zod (P-012):** upsert/promote валидируются `safeParse` ДО сети
 * (fail-fast даже при обходе типов). P-013: все URL относительные `/api/cutting/*`.
 */

export interface CuttingApi {
  /** Список проектов (cursor-пагинация + опц. фильтр status/search). */
  listProjects(query?: CuttingProjectListQuery): Promise<CuttingProjectListResponse>;
  /** Получить проект по id. */
  getProject(id: string): Promise<CuttingProject>;
  /** Upsert проекта (idempotent по client-UUID). */
  saveProject(data: CuttingProjectUpsertInput): Promise<CuttingProject>;
  /** Soft-delete проекта. */
  deleteProject(id: string): Promise<CuttingDeleteAck>;
  /** Список драфтов (полные — для восстановления). */
  listDrafts(query?: CuttingProjectListQuery): Promise<CuttingDraftListResponse>;
  /** Upsert драфта (idempotent по client-UUID). */
  saveDraft(data: CuttingDraftUpsert): Promise<CuttingDraft>;
  /** Promote драфта в проект (создаёт project из draft.data, удаляет драфт). */
  promoteDraft(id: string, data: CuttingDraftPromoteInput): Promise<CuttingProject>;
}

export function createCuttingApi(request: RequestFn): CuttingApi {
  return {
    listProjects: (query) => {
      const q = query ? CuttingProjectListQuerySchema.parse(query) : { limit: 50 };
      return request({
        method: 'GET',
        path: '/api/cutting/projects',
        query: {
          cursor: 'cursor' in q ? q.cursor : undefined,
          limit: q.limit,
          status: 'status' in q ? q.status : undefined,
          search: 'search' in q ? q.search : undefined,
        },
        responseSchema: CuttingProjectListResponseSchema,
        auth: true,
      });
    },

    getProject: (id) =>
      request({
        method: 'GET',
        path: `/api/cutting/projects/${id}`,
        responseSchema: CuttingProjectSchema,
        auth: true,
      }),

    saveProject: (data) => {
      const parsed = CuttingProjectUpsertSchema.safeParse(data);
      if (!parsed.success) return Promise.reject(parsed.error);
      return request({
        method: 'POST',
        path: '/api/cutting/projects',
        body: parsed.data,
        responseSchema: CuttingProjectSchema,
        auth: true,
      });
    },

    deleteProject: (id) =>
      request({
        method: 'POST',
        path: `/api/cutting/projects/${id}/delete`,
        responseSchema: CuttingDeleteAckSchema,
        auth: true,
      }),

    listDrafts: (query) => {
      const q = query ? CuttingProjectListQuerySchema.parse(query) : { limit: 50 };
      return request({
        method: 'GET',
        path: '/api/cutting/drafts',
        query: {
          cursor: 'cursor' in q ? q.cursor : undefined,
          limit: q.limit,
        },
        responseSchema: CuttingDraftListResponseSchema,
        auth: true,
      });
    },

    saveDraft: (data) => {
      const parsed = CuttingDraftUpsertSchema.safeParse(data);
      if (!parsed.success) return Promise.reject(parsed.error);
      return request({
        method: 'POST',
        path: '/api/cutting/drafts',
        body: parsed.data,
        responseSchema: CuttingDraftSchema,
        auth: true,
      });
    },

    promoteDraft: (id, data) => {
      const parsed = CuttingDraftPromoteSchema.safeParse(data);
      if (!parsed.success) return Promise.reject(parsed.error);
      return request({
        method: 'POST',
        path: `/api/cutting/drafts/${id}/promote`,
        body: parsed.data,
        responseSchema: CuttingProjectSchema,
        auth: true,
      });
    },
  };
}
